import {BaseChartWidget} from "@/libwyze-shared/charts/BaseChartWidget";
import dayjs from "dayjs";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "@/shared/BaseFragment";
import React from "react";
import {StyledBoxColumn, StyledSplitPaneRow} from "@/shared/StyledComponents";
import {Typography} from "@mui/material";
import {PD_LG} from "@/shared/dimens";
import {
  Appointments,
  PatientProfiles,
  PracticeData,
  TransactionActionType,
  Transactions
} from "../../libwyze-shared/types";

type HeadWidgetInfo = {
  title: string,
  text: string,
}

type BaseHeadWidgetProps = BaseFragmentProps & {}

type BaseHeadWidgetState = BaseFragmentState & {
  infos: HeadWidgetInfo[],
}

class BaseHeadWidget<P extends BaseHeadWidgetProps = BaseHeadWidgetProps, S extends BaseHeadWidgetState = BaseHeadWidgetState> extends BaseFragment<P, S> {

  protected renderContainerContent(): React.ReactElement | null {
    return <StyledSplitPaneRow style={{padding: PD_LG}}>
      {this.state.infos?.map(info => <StyledBoxColumn
        style={{alignItems: "center", justifyContent: "center", flexGrow: 1}}>
        <Typography variant="h2">{info.title}</Typography>
        <Typography variant="caption">{info.text}</Typography>
      </StyledBoxColumn>)}
    </StyledSplitPaneRow>;
  }
}

export class RevenueWidget extends BaseHeadWidget {

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const now = dayjs();
    const transactions = await new Transactions(PracticeData.provider().practiceId).getOrLoadListItems();
    let count = 0;
    for (const transaction of transactions) {
      if (transaction.actionType !== TransactionActionType.CHARGE && transaction.actionType !== TransactionActionType.PROCEDURE) {
        continue;
      }
      const date = dayjs(transaction.transactionAt);
      const diff = now.diff(date, "day");
      if (diff < 0) {
        continue;
      }
      if (diff >= 7) {
        break;
      }
      count++;
    }
    this.setState({
      infos: [{
        title: count.toString(),
        text: "7-Day Sum • Source: Ledger",
      }],
    });
  }
}

export class AppointmentsWidget extends BaseHeadWidget {

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const now = dayjs();
    const appointments = await new Appointments(PracticeData.provider().practiceId).getOrLoadListItems();
    let count = 0;
    for (const appointment of appointments) {
      const date = dayjs(appointment.scheduled);
      const diff = now.diff(date, "day");
      if (diff < 0) {
        continue;
      }
      if (diff >= 7) {
        break;
      }
      count++;
    }
    this.setState({
      infos: [{
        title: count.toString(),
        text: "7-Day Sum • Source: Calendar",
      }],
    });
  }
}

export class ActivePatientsWidget extends BaseHeadWidget {

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const patients = (await new PatientProfiles(PracticeData.provider().practiceId).getOrLoadListItems());
    this.setState({
      infos: [{
        title: patients.length.toString(),
        text: "Source: Patient Directory",
      }],
    });
  }
}

export class DailyRevenueWidget extends BaseChartWidget {

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const now = dayjs();
    const transactions = await new Transactions(PracticeData.provider().practiceId).getOrLoadListItems();
    const dailyProcedures: number[] = new Array(7).fill(0);
    for (const transaction of transactions) {
      if (transaction.actionType !== TransactionActionType.PROCEDURE) {
        continue;
      }
      const date = dayjs(transaction.transactionAt);
      const diff = now.diff(date, "day");
      if (diff < 0) {
        continue;
      }
      if (diff >= 7) {
        break;
      }
      dailyProcedures[diff]++;
    }
    dailyProcedures.reverse();
    const dayLabels = new Array(7).fill(null).map((_, index) => now.add(index, 'day').format("ddd"));
    dayLabels.reverse();
    this.setState({
      xSeries: dayLabels,
      ySeries: dailyProcedures,
    });
  }
}

export class DailyAppointmentsWidget extends BaseChartWidget {

  protected async fetchOnMount(forceReload?: boolean): Promise<any> {
    const now = dayjs();
    const appointments = await new Appointments(PracticeData.provider().practiceId).getOrLoadListItems();
    const dailyAppointments: number[] = new Array(7).fill(0);
    for (const appointment of appointments) {
      const date = dayjs(appointment.scheduled);
      const diff = now.diff(date, "day");
      if (diff < 0) {
        continue;
      }
      if (diff >= 7) {
        break;
      }
      dailyAppointments[diff]++;
    }
    dailyAppointments.reverse();
    const dayLabels = new Array(7).fill(null).map((_, index) => now.add(index, 'day').format("ddd"));
    dayLabels.reverse();
    this.setState({
      xSeries: dayLabels,
      ySeries: dailyAppointments,
    });
  }
}